import React from 'react';
import { Box, Typography, Container, Card, CardContent, useMediaQuery, useTheme as useMuiTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import SEO from '../components/SEO';
import Header from '../components/Header';
import { getAllArticles } from '../data/articles';

const Articles: React.FC = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const articles = getAllArticles();

  const colors = {
    bg: isDark ? '#0f0f0f' : '#f5f5f5',
    cardBg: isDark ? '#1a1a1a' : '#ffffff',
    text: isDark ? '#e0e0e0' : '#1a1a1a',
    textSecondary: isDark ? '#999' : '#666',
    border: isDark ? '#2a2a2a' : '#e0e0e0',
    hover: isDark ? '#252525' : '#f8f8f8',
    accent: isDark ? '#C084FC' : '#9333EA',
  };

  // Collection page structured data
  const collectionStructuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Articles - Guides to Elegant Erotic Fiction",
    "description": "Expert guides to sophisticated adult fiction. Learn about elegant erotica, romantic stories, and literary adult content for discerning readers.",
    "url": "https://dailystories.fr/articles",
    "publisher": {
      "@type": "Organization",
      "name": "Daily Stories",
      "logo": {
        "@type": "ImageObject",
        "url": "https://dailystories.fr/podcast.png"
      }
    },
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": articles.map((article, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "url": `https://dailystories.fr/articles/${article.slug}`,
        "name": article.title
      }))
    }
  };

  // Breadcrumb structured data
  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://dailystories.fr"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Articles",
        "item": "https://dailystories.fr/articles"
      }
    ]
  };

  return (
    <>
      <SEO 
        title="Articles - Guides to Elegant Erotic Fiction | Daily Stories"
        description="Expert guides to sophisticated adult fiction. Learn about elegant erotica, romantic stories, and literary adult content for discerning readers."
        keywords="erotica guides, adult fiction articles, elegant erotica guide, romantic erotica, literary erotica articles"
        url="https://dailystories.fr/articles"
      />
      
      {/* Collection Page Schema */}
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(collectionStructuredData) }}
      />
      
      {/* Breadcrumb Schema */}
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbStructuredData) }}
      />
      
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: colors.bg
      }}>
        <Header isMobile={isMobile} />

        <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 }, px: { xs: 3, md: 4 } }}>
          {/* Hero Section */}
          <Box sx={{ mb: 6, textAlign: 'center' }}>
            <Typography 
              variant="h3" 
              sx={{ 
                color: colors.text,
                fontWeight: 600,
                mb: 2,
                fontSize: { xs: '2rem', md: '2.5rem' }
              }}
            >
              Guides to Elegant Erotica
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                color: colors.textSecondary,
                fontWeight: 400,
                maxWidth: '600px',
                mx: 'auto'
              }}
            >
              Expert articles about sophisticated adult fiction, romantic erotica, and literary sensuality
            </Typography>
          </Box>

          {/* Articles Grid */}
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)'
            },
            gap: 3
          }}>
            {articles.map((article) => (
              <Card
                key={article.id}
                onClick={() => navigate(`/articles/${article.slug}`)}
                sx={{
                  backgroundColor: colors.cardBg,
                  border: `1px solid ${colors.border}`,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: isDark 
                      ? '0 8px 24px rgba(0,0,0,0.4)' 
                      : '0 8px 24px rgba(0,0,0,0.12)',
                  }
                }}
              >
                {/* Article Image */}
                {article.imageUrl && (
                  <Box
                    component="img"
                    src={article.imageUrl}
                    alt={article.imageAlt || article.title}
                    sx={{
                      width: '100%',
                      height: '200px',
                      objectFit: 'cover',
                      display: 'block'
                    }}
                  />
                )}

                <CardContent sx={{ 
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  flexGrow: 1
                }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: colors.text,
                      fontWeight: 600,
                      mb: 2,
                      fontSize: '1.1rem',
                      lineHeight: 1.3
                    }}
                  >
                    {article.title}
                  </Typography>
                  
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: colors.textSecondary,
                      mb: 3,
                      lineHeight: 1.6,
                      fontSize: '0.9rem',
                      flexGrow: 1
                    }}
                  >
                    {article.excerpt}
                  </Typography>

                  <Box sx={{ 
                    display: 'flex', 
                    gap: 1.5, 
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    pt: 2,
                    borderTop: `1px solid ${colors.border}`,
                    fontSize: '0.8rem'
                  }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: colors.textSecondary,
                        fontSize: '0.8rem'
                      }}
                    >
                      {article.readTime}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: colors.accent,
                        fontWeight: 500,
                        fontSize: '0.8rem'
                      }}
                    >
                      Read →
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default Articles;
